import React, { useState } from 'react';

const processSteps = [
    {
        icon: 'fa-user-graduate',
        title: 'Career Counseling',
        storyTitle: 'You start with clarity',
        hook: 'We map your strengths, interests, and long-term vision.',
        description: 'Expert guidance to choose the right career path.',
        outcome: 'Outcome: A focused career direction with confidence.',
        visual: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200&q=90',
    },
    {
        icon: 'fa-university',
        title: 'University Selection',
        storyTitle: 'Then we shortlist your best-fit campuses',
        hook: 'Your profile, budget, and goals shape every recommendation.',
        description: 'Get matched with universities aligned to your profile and budget.',
        outcome: 'Outcome: A smart shortlist built for admits and value.',
        visual: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=1200&q=90',
    },
    {
        icon: 'fa-file-alt',
        title: 'Application Process',
        storyTitle: 'Your profile becomes a winning application',
        hook: 'Every document is refined to highlight your strengths.',
        description: 'SOP, LOR, documentation, and complete submission support.',
        outcome: 'Outcome: Strong applications submitted on time.',
        visual: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&q=90',
    },
    {
        icon: 'fa-passport',
        title: 'Visa Processing',
        storyTitle: 'We prepare you for visa success',
        hook: 'Documentation, filing, and mock interview support end-to-end.',
        description: 'Expert visa guidance with documentation and interview prep.',
        outcome: 'Outcome: A smoother visa journey with fewer surprises.',
        visual: 'https://images.unsplash.com/photo-1562684393-b040c153204c?w=1200&q=90',
    },
    {
        icon: 'fa-plane-departure',
        title: 'Pre-Departure Support',
        storyTitle: 'Finally, you are ready to launch',
        hook: 'We guide your transition from home to campus life.',
        description: 'Orientation for accommodation, travel, and settling in.',
        outcome: 'Outcome: You arrive prepared, confident, and settled.',
        visual: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=90',
    },
];

const StickyProcessSection = () => {
    const [activeStep, setActiveStep] = useState(0);
    const step = processSteps[activeStep];
    const progress = Math.round(((activeStep + 1) / processSteps.length) * 100);

    return (
        <section className="relative py-16 md:py-20">
            {/* Fixed background layer */}
            <div className="absolute inset-0 bg-gradient-to-b from-lightGray to-white -z-10"></div>
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-7 md:mb-9 text-center">
                    <div className="inline-flex items-center gap-3 rounded-full border border-primary-red/20 bg-white px-4 py-2 mb-4">
                        <i className="fas fa-route text-primary-red"></i>
                        <span className="text-sm font-semibold text-black">Our Process</span>
                    </div>
                    <h2 className="text-3xl md:text-5xl font-bold text-black max-w-4xl mx-auto leading-tight">
                        A step-by-step journey to your dream university
                    </h2>
                </div>

                <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-6 lg:gap-8 items-stretch">
                    <div className="bg-white rounded-2xl shadow-lg p-4 md:p-5 border border-gray-100">
                        <div className="flex items-center justify-between text-sm text-gray mb-3">
                            <span className="font-semibold">Progress</span>
                            <span className="font-semibold text-black">{progress}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
                            <div
                                className="h-full bg-gradient-to-r from-primary-red to-primary-light transition-all duration-500"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>

                        <div className="space-y-2.5">
                            {processSteps.map((item, index) => {
                                const isActive = index === activeStep;
                                return (
                                    <button
                                        key={item.title}
                                        type="button"
                                        onClick={() => setActiveStep(index)}
                                        onMouseEnter={() => setActiveStep(index)}
                                        className={`w-full text-left group flex items-center gap-3 rounded-xl p-3 border transition-all duration-300 ${isActive
                                            ? 'bg-red-50 border-primary-red/40 shadow-sm'
                                            : 'bg-white border-gray-200 hover:border-primary-red/30'
                                            }`}
                                    >
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${isActive
                                            ? 'bg-primary-red text-white'
                                            : 'bg-gray-100 text-gray-500'
                                            }`}>
                                            {index + 1}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className={`text-xs uppercase tracking-wide mb-0.5 ${isActive ? 'text-primary-red font-semibold' : 'text-gray-400'}`}>
                                                Step {index + 1} of {processSteps.length}
                                            </p>
                                            <h3 className={`font-semibold leading-snug ${isActive ? 'text-black' : 'text-gray-700'}`}>
                                                {item.title}
                                            </h3>
                                        </div>
                                        <i className={`fas ${item.icon} ${isActive ? 'text-primary-red' : 'text-gray-300'}`}></i>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="relative min-h-[420px] md:min-h-[540px] rounded-2xl overflow-hidden shadow-2xl">
                        <img
                            src={step.visual}
                            alt={step.title}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent"></div>

                        <div className="absolute inset-x-0 bottom-0 p-5 md:p-7 text-white">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-sm font-semibold mb-2">
                                <span>Step {activeStep + 1} of {processSteps.length}</span>
                            </div>
                            <h3 className="text-2xl md:text-5xl font-bold mb-1 leading-tight">{step.title}</h3>
                            <p className="text-red-100 font-semibold mb-2 text-lg md:text-2xl">{step.storyTitle}</p>
                            <p className="text-gray-100 leading-relaxed mb-2 text-base md:text-lg">{step.hook}</p>
                            <p className="text-gray-200 leading-relaxed text-base md:text-lg">{step.description}</p>
                            <p className="text-red-100 font-semibold mt-3 text-base md:text-lg">{step.outcome}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default StickyProcessSection;
